import { ApiResponse } from '@/domain'
import {
  AgreementFilters,
  AgreementStatus,
  CreateAgreementData,
  CremationFilters,
  SubscriptionFilters,
  UpdateAgreementData,
} from '@/domain/agreements/agreement'
import { DeathData } from '@/domain/deathData/deathData'
import { ServiceType } from '@/domain/offerings/offering'
import { Currency, PaymentMethod, PaymentStatus } from '@/domain/payments/payments'
import { useToast } from '@/hooks/use-toast'
import { AgreementAction, AgreementActionRequest, agreementsApi, CreateAgreementRequest, CreateMultiAgreementRequest, CreatePaymentForAgreementDto } from '@/rest/modules/agreements-api'
import { Paths } from '@/routes/paths'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'

export const agreementKeys = {
  all: ['agreements'] as const,
  lists: () => [...agreementKeys.all, 'list'] as const,
  list: (filters: AgreementFilters) => [...agreementKeys.lists(), { filters }] as const,
  details: () => [...agreementKeys.all, 'detail'] as const,
  detail: (id: string) => [...agreementKeys.details(), id] as const,
  byClient: (clientId: string) => [...agreementKeys.all, 'client', clientId] as const,
  byOffering: (offeringId: string) => [...agreementKeys.all, 'offering', offeringId] as const,
  documents: (agreementId: string) => [...agreementKeys.all, 'documents', agreementId] as const,
  stats: (filters?: AgreementFilters) => [...agreementKeys.all, 'stats', { filters }] as const,
  subscriptions: (filters: SubscriptionFilters) => [...agreementKeys.all, 'subscriptions', { filters }] as const,
  cremations: (filters: CremationFilters) => [...agreementKeys.all, 'cremations', { filters }] as const,
  paymentPlans: (filters: SubscriptionFilters) => [...agreementKeys.all, 'paymentPlans', { filters }] as const,
}

export const useAgreements = (filters?: AgreementFilters) => {
  return useQuery({
    queryKey: agreementKeys.list(filters || {}),
    queryFn: () => agreementsApi.getAgreements(filters),
    staleTime: 1000 * 60 * 5, // 5 minutes
  })
}

export const useDataSubscriptions = (filters?: SubscriptionFilters) => {
  return useQuery({
    queryKey: agreementKeys.subscriptions(filters || {}),
    queryFn: () => agreementsApi.getDataSubscriptions(filters),
    staleTime: 1000 * 60 * 5, // 5 minutes
  })
}

export const useDataCremations = (filters?: CremationFilters) => {
  return useQuery({
    queryKey: agreementKeys.cremations(filters || {}),
    queryFn: () => agreementsApi.getDataCremations(filters),
    staleTime: 1000 * 60 * 5, // 5 minutes
  })
}

export const useAgreement = (id: string) => {
  return useQuery({
    queryKey: agreementKeys.detail(id),
    queryFn: () => agreementsApi.getAgreement(id),
    enabled: !!id,
  })
}

export const useAgreementsByClient = (clientId: string) => {
  return useQuery({
    queryKey: agreementKeys.byClient(clientId),
    queryFn: () => agreementsApi.getAgreementsByClient(clientId),
    enabled: !!clientId,
  })
}

export const useAgreementDocuments = (agreementId: string) => {
  return useQuery({
    queryKey: agreementKeys.documents(agreementId),
    queryFn: () => agreementsApi.getAgreementDocuments(agreementId),
    enabled: !!agreementId,
    staleTime: 1000 * 60 * 5, // 5 minutes
  })
}

export const useAgreementStats = (filters?: AgreementFilters) => {
  return useQuery({
    queryKey: agreementKeys.stats(filters),
    queryFn: () => agreementsApi.getAgreementStats(filters),
    staleTime: 1000 * 60 * 5, // 5 minutes
  })
}

export const useLatestContractNumber = (serviceType?: ServiceType) => {
  return useQuery({
    queryKey: [...agreementKeys.all, 'latestContractNumber', serviceType],
    queryFn: () => agreementsApi.getLatestContractNumber(serviceType!),
    staleTime: 100,
    enabled: !!serviceType
  })
}

export const useCreateAgreement = () => {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: (data: CreateAgreementData) => {
      const request: CreateAgreementRequest = {
        agreement: {
          userId: data.userId,
          offeringId: data.offeringId,
          notes: data.notes,
          startDate: data.startDate,
          endDate: data.endDate,
          status: AgreementStatus.Pending,
          amount: data.amount,
          mainContractId: data.mainContractId,
          contractNumber: data.contractNumber,
          sellCode: data.sellCode,
          sellerId: data.sellerId,
          collectorId: data.collectorId,
        },
        payment: {
          userId: data.userId,
          amount: data.amount,
          status: PaymentStatus.PENDING,
          paymentMethod: PaymentMethod.OTHER,
          currency: Currency.ARS,
        },
        deathData: data.deathData ? [...data.deathData] : [],
        assuredContract: data.assuredPeople && data.assuredPeople.length > 0 ? {
          people: [...data.assuredPeople]
        } : undefined,
        paymentPlan: data.installmentAmount && data.installments ? {
          installmentAmount: data.installmentAmount,
          installments: data.installments,
          initialAmount: data.initialAmount || 0
        } : undefined,
        parcel: data.parcelId ? {
          parcelId: data.parcelId,
        } : undefined
      }
      return agreementsApi.createAgreement(request)
    },
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: agreementKeys.lists() })
      queryClient.invalidateQueries({ queryKey: agreementKeys.stats() })
      toast({
        title: 'Éxito',
        description: 'Contratación creada correctamente',
      })
      return response
    },
    onError: (response: ApiResponse<any>) => {
      toast({
        title: 'Error',
        description: response.message || response.error || 'Error al crear la contratación',
        variant: 'destructive',
      })
    },
  })
}

export const useCreateMultiAgreement = () => {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: (data: CreateMultiAgreementRequest & { deathData?: DeathData[] }) => {
      const request: CreateMultiAgreementRequest = {
        agreements: data.agreements,
        paymentPlan: data.paymentPlan,
        parcel: data.parcel,
        deathData: data.deathData,
        updateDeathData: data.updateDeathData
      }
      return agreementsApi.createMultiAgreement(request)
    },
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: agreementKeys.lists() })
      queryClient.invalidateQueries({ queryKey: agreementKeys.stats() })
      toast({
        title: 'Éxito',
        description: 'Contrataciones creadas correctamente',
      })
      return response
    },
    onError: (response: ApiResponse<any>) => {
      toast({
        title: 'Error',
        description: response.message || response.error || 'Error al crear las contrataciones',
        variant: 'destructive',
      })
    },
  })
}

export const useCreateAgreementPayment = () => {
  const { toast } = useToast()
  const navigate = useNavigate()

  return useMutation({
    mutationFn: (req: CreatePaymentForAgreementDto) =>
      agreementsApi.createAgreementPayment(req),
    onSuccess: (response, variables) => {
      navigate({ to: `${Paths.Management.Contracts}/${variables.agreementId}/${Paths.Management.Payments}/${response.payment.id}` })
    },
    onError: (response: ApiResponse<any>) => {
      toast({
        title: 'Error',
        description: response.message || response.error || 'Error al crear la contratación',
        variant: 'destructive',
      })
    },
  })
}

export const useUpdateAgreement = () => {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateAgreementData }) =>
      agreementsApi.updateAgreement(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: agreementKeys.detail(variables.id) })
      queryClient.invalidateQueries({ queryKey: agreementKeys.lists() })
      queryClient.invalidateQueries({ queryKey: agreementKeys.stats() })
      toast({
        title: 'Éxito',
        description: 'Contratación actualizada correctamente',
      })
    },
    onError: (response: ApiResponse<any>) => {
      toast({
        title: 'Error',
        description: response.message || response.error || 'Error al actualizar la contratación',
        variant: 'destructive',
      })
    },
  })
}

export const useDeleteAgreement = () => {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: (id: string) => agreementsApi.deleteAgreement(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: agreementKeys.lists() })
      queryClient.invalidateQueries({ queryKey: agreementKeys.stats() })
      toast({
        title: 'Éxito',
        description: 'Contratación eliminada correctamente',
      })
    },
    onError: (response: ApiResponse<any>) => {
      toast({
        title: 'Error',
        description: response.message || response.error || 'Error al eliminar la contratación',
        variant: 'destructive',
      })
    },
  })
}

export const useUploadDocument = () => {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: ({ agreementId, file }: { agreementId: string; file: File }) =>
      agreementsApi.uploadDocument(agreementId, file),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: agreementKeys.detail(variables.agreementId) })
      toast({
        title: 'Éxito',
        description: 'Documento subido correctamente',
      })
    },
    onError: (response: ApiResponse<any>) => {
      const errorMessage = 'Error al subir el documento'
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      })
    },
  })
}

export const useDeleteDocument = () => {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: ({ agreementId, documentId }: { agreementId: string; documentId: string }) =>
      agreementsApi.deleteDocument(agreementId, documentId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: agreementKeys.detail(variables.agreementId) })
      toast({
        title: 'Éxito',
        description: 'Documento eliminado correctamente',
      })
    },
    onError: (response: ApiResponse<any>) => {
      toast({
        title: 'Error',
        description: response.message || response.error || 'Error al eliminar el documento',
        variant: 'destructive',
      })
    },
  })
}

export const useAgreementAction = () => {
  const queryClient = useQueryClient()
  const { toast } = useToast()
  const nav = useNavigate()

  return useMutation({
    mutationFn: (data: AgreementActionRequest) =>
      agreementsApi.postAgreementAction(data),
    onSuccess: (response: any, { contractId, action }) => {
      queryClient.invalidateQueries({ queryKey: agreementKeys.detail(contractId) })
      queryClient.invalidateQueries({ queryKey: agreementKeys.lists() })
      queryClient.invalidateQueries({ queryKey: agreementKeys.stats() })
      const messages: Record<AgreementAction, string> = {
        modify_subscription: 'Contratación suspendida correctamente',
        modify_status: 'Contratación finalizada correctamente',
        redeem_people: 'Redención procesada correctamente',
        modify_parcel: 'Ubicación de parcela actualizada correctamente',
      }
      toast({
        title: 'Éxito',
        description: messages[action],
      })
      if (response?.redemption?.id) {
        nav({ to: `${Paths.Management.Contracts}/${contractId}/redencion/${response?.redemption?.id}` })
      }
    },
    onError: (response: ApiResponse<any>) => {
      toast({
        title: 'Error',
        description: response.message || response.error || 'Error al realizar la acción',
        variant: 'destructive',
      })
    },
  })
}